import { useState, useEffect } from "react";
import {
  Search, Download, X, RotateCcw, InfoIcon,
} from "lucide-react";
import { useOptions } from "../contexts/OptionsContext";
import { getStudentResults, searchStudentResultsById } from "../api/services/studentsService";
import ColumnFilterDropdown from "../components/ColumnFilterDropdown";
import ScoreRangeFilterDropdown from "../components/ScoreRangeFilterDropdown";
import {
  DEFAULT_RESULT_FILTERS, RESULT_FILTER_MAP, STATUS_LABELS,
  COLUMN_INFO, CHIP_PREFIX, EXPORT_HEADERS,
} from "../utils/constants";
import {
  toTitleCase, getHiddenFilters, buildExportRow,
  downloadCSV, getFilterLabel,
} from "../utils/helpers";

const TableHead = ({ children, filters, optionsMap, onFilterApply, onScoreRangeApply }) => {
  const [showTip, setShowTip] = useState(false);
  const tip = COLUMN_INFO[children];
  const fConfig = RESULT_FILTER_MAP[children];

  return (
    <th className="adm-th">
      <span className="inline-flex items-center gap-1">
        {children}
        {tip && (
          <span className="relative" onMouseEnter={() => setShowTip(true)} onMouseLeave={() => setShowTip(false)}>
            <InfoIcon size={13} className="hover:opacity-70 transition-opacity" />
            {showTip && (
              <div className="absolute top-full left-0 mt-2 z-[999] w-75 normal-case font-normal"
                style={{ background: "var(--screen-background-color)", border: "1px solid rgba(190,5,250,0.15)", borderRadius: "10px", boxShadow: "0 4px 20px rgba(0,0,0,0.15)", padding: "12px 14px" }}>
                <div className="flex items-center gap-2 mb-2 pb-2" style={{ borderBottom: "1px solid rgba(190,5,250,0.1)" }}>
                  <div className="w-1 h-3.5 rounded-full" style={{ background: "var(--buttons-background-color)" }} />
                  <p className="text-xs font-bold text-text-main uppercase tracking-wider">{children}</p>
                </div>
                <div className="space-y-1.5">
                  {tip.split(" • ").map((line, i) => {
                    const [label, ...rest] = line.split(": ");
                    return (
                      <div key={i} className="flex items-start gap-2">
                        <span className="mt-1.5 w-1 h-1 rounded-full flex-shrink-0" style={{ background: "var(--buttons-background-color)" }} />
                        <p className="text-xs text-text-sub leading-relaxed">
                          {rest.length > 0 ? (<><span className="font-semibold text-text-main">{label}:</span> {rest.join(": ")}</>) : line}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </span>
        )}
        {fConfig && optionsMap[fConfig.optionsKey] && !getHiddenFilters(filters.status).includes(fConfig.key) && (
          <ColumnFilterDropdown column={children} options={optionsMap[fConfig.optionsKey]}
            currentValue={filters[fConfig.key]} defaultVal={fConfig.defaultVal}
            onApply={(val) => onFilterApply(fConfig.key, val)} multiSelect={fConfig.multiSelect || false} />
        )}
        {children === "Overall Score" && (
          <ScoreRangeFilterDropdown currentMin={filters.scoreMin} currentMax={filters.scoreMax} onApply={onScoreRangeApply} />
        )}
      </span>
    </th>
  );
};

function Chip({ label, onClear }) {
  const [prefix, ...rest] = label.includes(":") ? label.split(":") : ["", label];
  const value = rest.join(":").trim();

  return (
    <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-primary text-btn-text border border-primary/20">
      {prefix && <span className="font-semibold">{prefix}:</span>}
      <span className="font-medium">{value}</span>
      <button onClick={onClear} className="hover:bg-white/20 rounded-full p-0.5 ml-0.5 cursor-pointer"><X size={11} /></button>
    </div>
  );
}

function StudentsEvaluationResults() {
  const {
    departments, years, semesters, programmingLanguages, databases, assessmentStatuses,
    aptitudeLevels, communicationLevels, programmingLevels, databaseLevels,
    getDepartmentLabel, getYearLabel, getSemesterLabel, overallStatuses,
  } = useOptions();
  const [exporting, setExporting] = useState(false);

  const optionsMap = {
    departments: departments || [], years: years || [], semesters: semesters || [],
    programmingLanguages: programmingLanguages || [], databases: databases || [],
    assessmentStatuses: assessmentStatuses || [], aptitudeLevels: aptitudeLevels || [],
    communicationLevels: communicationLevels || [], programmingLevels: programmingLevels || [],
    databaseLevels: databaseLevels || [], overallStatuses: overallStatuses || [],
  };

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_RESULT_FILTERS);
  const [pagination, setPagination] = useState({ page: 1, page_size: 100, total_records: 0, total_pages: 1 });

  const f = filters;
  const hasActiveFilters = Object.keys(DEFAULT_RESULT_FILTERS).some((k) => f[k] !== DEFAULT_RESULT_FILTERS[k]);

  const fetchResults = async (page = 1, currentFilters) => {
    try {
      setLoading(true);
      const rf = currentFilters || filters;
      const r = await getStudentResults({ ...rf, page, page_size: 100 });
      if (r.success) {
        setResults(r.data.results || []);
        setPagination({ page: r.data.page, page_size: r.data.page_size || 100, total_records: r.data.total_records, total_pages: r.data.total_pages });
      }
    } catch (e) { console.error("Error fetching results:", e); }
    finally { setLoading(false); }
  };

  useEffect(() => { console.log("StudentsEvaluationResults mounted");  fetchResults(1, DEFAULT_RESULT_FILTERS); }, []);

  const handleFilterApply = (key, value) => {
    const updated = { ...filters, [key]: value };
    if (key === "status") {
      const hidden = getHiddenFilters(value);
      hidden.forEach((k) => {
        const entry = Object.values(RESULT_FILTER_MAP).find((f) => f.key === k);
        if (entry) updated[k] = entry.defaultVal;
      });
    }
    setFilters(updated); setIsSearchMode(false); setSearchQuery(""); fetchResults(1, updated);
  };

  const handleSearch = async (query) => {
    if (!query.trim()) { fetchResults(1, filters); return; }
    setFilters(DEFAULT_RESULT_FILTERS); setIsSearchMode(true);
    try { setLoading(true); const r = await searchStudentResultsById(query.trim()); setResults(r.success ? r.data.results || [] : []); }
    catch (e) { console.error("Search error:", e); }
    finally { setLoading(false); }
  };

  const clearAllFilters = () => { setSearchQuery(""); setIsSearchMode(false); setFilters(DEFAULT_RESULT_FILTERS); fetchResults(1, DEFAULT_RESULT_FILTERS); };
  const clearFilter = (key, defaultValue) => { const updated = { ...filters, [key]: defaultValue }; setFilters(updated); fetchResults(1, updated); };

  const handleScoreRangeApply = (min, max) => {
    const scoreMin = min === "" && max !== "" ? "0" : min;
    const scoreMax = max === "" && min !== "" ? "100" : max;
    const updated = { ...filters, scoreMin, scoreMax };
    setFilters(updated); setIsSearchMode(false); setSearchQuery(""); fetchResults(1, updated);
  };

  const exportResultsToCSV = async () => {
    setExporting(true);
    try {
      let allResults = [];
      let currentPage = 1;
      let totalPages = 1;
  
      do {
        const r = await getStudentResults({ ...filters, page: currentPage, page_size: 100 });
        if (r.success) {
          allResults = [...allResults, ...(r.data.results || [])];
          totalPages = r.data.total_pages || 1;
        } else break;
        currentPage++;
        if (currentPage <= totalPages) await new Promise((res) => setTimeout(res, 300));
      } while (currentPage <= totalPages);
  
      if (!allResults.length) return;
      downloadCSV(EXPORT_HEADERS, allResults.map(buildExportRow), `evaluation_results_${new Date().toISOString().split("T")[0]}.csv`);
    } catch (e) {
      console.error("Export error:", e);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      {/* Action Bar */}
      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1 max-w-xs">
          <Search size={16} className="absolute left-3 top-1/4 -translate-y-1/2 text-text-sub" />
          <input type="text" autoComplete="off" placeholder="Search by Student ID..."
            value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleSearch(searchQuery); }}
            className="w-full pl-9 pr-8 py-2 rounded-lg text-sm text-text-main placeholder:text-text-sub outline-none transition-colors"
            style={{ background: "var(--screen-background-color)", color: "var(--text-primary)", border: "2px solid rgba(190, 5, 250, 0.2)" }}
            onFocus={(e) => e.target.style.borderColor = "var(--buttons-background-color)"}
            onBlur={(e) => e.target.style.borderColor = "rgba(190, 5, 250, 0.2)"} />
          {searchQuery && (
            <button onClick={() => { setSearchQuery(""); setIsSearchMode(false); fetchResults(1, filters); }}
              className="absolute right-2 top-1/4 -translate-y-1/2 p-0.5 -mt-1 rounded-full bg-primary text-btn-text cursor-pointer"><X size={12} /></button>
          )}
        </div>
        <button onClick={() => handleSearch(searchQuery)} disabled={!searchQuery.trim()}
          className="px-3 py-2 rounded-lg bg-primary text-btn-text text-sm font-medium cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-all">Search</button>
          {results.length > 0 && (
              <button onClick={exportResultsToCSV} disabled={exporting}
                className="px-3 py-2 rounded-lg bg-primary border border-purple-glow-15 text-sm text-btn-text flex items-center gap-1.5 hover:opacity-90 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                {exporting ? (
                  <><div className="w-4 h-4 rounded-full flex-shrink-0" style={{ border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "white", animation: "spin 0.6s linear infinite" }} /> Exporting...</>                ) : (
                  <><Download size={14} /> Export</>
                )}
              </button>
            )}
        {hasActiveFilters && (
          <>
            <button onClick={clearAllFilters}
                className="px-3 py-2 rounded-lg text-sm text-red-400 bg-red-500/10 border border-red-500/20 hover:bg-red-500/20 transition-colors cursor-pointer flex items-center gap-1">
              <RotateCcw size={13} /> Clear
            </button>
          </>
        )}
      </div>

      {/* Expandable filter chips */}
      {hasActiveFilters &&  (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {Object.entries(RESULT_FILTER_MAP).map(([, { key, defaultVal }]) => {
            if (f[key] === defaultVal) return null;
            const prefix = CHIP_PREFIX[key];
            const label = prefix
            ? `${prefix}: ${getFilterLabel(key, f[key], { getDepartmentLabel, getYearLabel, getSemesterLabel })}`
            : getFilterLabel(key, f[key], { getDepartmentLabel, getYearLabel, getSemesterLabel });
            return <Chip key={key} label={label} onClear={() => clearFilter(key, defaultVal)} />;
          })}
          {(f.scoreMin !== "" || f.scoreMax !== "") && (
            <Chip label={`Score: ${f.scoreMin !== "" && f.scoreMax !== "" ? `${f.scoreMin}–${f.scoreMax}%` : f.scoreMin !== "" ? `≥${f.scoreMin}%` : `≤${f.scoreMax}%`}`}
              onClear={() => { const updated = { ...filters, scoreMin: "", scoreMax: "" }; setFilters(updated); fetchResults(1, updated); }} />
          )}
        </div>
      )}

      {/* Table */}
      <div className="bg-card-bg rounded-xl border border-purple-glow-10 flex flex-col flex-1 min-h-0 overflow-hidden shadow-sm">
        <div className="overflow-auto flex-1">
          <table className="w-full min-w-max">
            <thead className="adm-thead sticky top-0 z-10">
              <tr>
                {["Student ID","Name","Status","Language","Database","Programming","Database Level","Communication","Aptitude","Overall Status","Overall Score","Created At","Completed At","Branch","GRADUATION YEAR","Semester","Email"].map((h) => (
                  <TableHead key={h} filters={filters} optionsMap={optionsMap} onFilterApply={handleFilterApply} onScoreRangeApply={handleScoreRangeApply}>{h}</TableHead>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="17" className="px-6 py-16 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-7 h-7 rounded-full border-[3px] border-purple-500/20 border-t-purple-500 animate-spin" />
                    <p className="text-text-sub text-sm">Loading...</p>
                  </div>
                </td></tr>
              ) : results.length === 0 ? (
                <tr><td colSpan="17" className="px-6 py-12 text-center text-text-sub text-sm">No results found</td></tr>
              ) : (
                results.map((s, i) => (
                  <tr key={`${s._id || s.id}-${i}`} className="adm-tr">
                    <td className="adm-td font-medium text-text-main">{s.student_id}</td>
                    <td className="adm-td font-medium text-text-main">{s.name}</td>
                    <td className="adm-td">
                    <td className="adm-td">
                      {s.status?.toLowerCase() === "completed" ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold text-green-500">Completed</span>
                        : s.status?.toLowerCase() === "pending" ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold text-orange-500">Pending</span>
                        : s.status?.toLowerCase() === "incomplete left" ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold text-red-400">Incomplete</span>
                        : s.status?.toLowerCase() === "not started" ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold text-yellow-500">Not Started</span>
                        : <span className="text-text-sub">{s.status || "-"}</span>}
                    </td>
                    </td>
                    <td className="adm-td text-text-sub">{toTitleCase(s.language || "-")}</td>
                    <td className="adm-td text-text-sub">{toTitleCase(s.database || "-")}</td>
                    <td className="adm-td text-text-sub">{s.programming_level || "-"}</td>
                    <td className="adm-td text-text-sub">{s.database_level || "-"}</td>
                    <td className="adm-td text-text-sub">{s.soft_skills_level || "-"}</td>
                    <td className="adm-td text-text-sub">{s.aptitude_level || "-"}</td>
                    <td className="adm-td">
                      {s.overall_status ? (
                        <span className={`font-bold text-xs ${s.overall_status === "Needs Training" ? "text-yellow-500" : s.overall_status === "Interview Ready" ? "text-green-500" : s.overall_status === "Developmental" ? "text-orange-500" : "text-text-sub"}`}>{s.overall_status}</span>
                      ) : <span className="text-text-sub">-</span>}
                    </td>
                    <td className="adm-td font-medium text-text-main">{s.overall_score != null ? `${s.overall_score}%` : "-"}</td>
                    <td className="adm-td text-text-sub text-xs">{s.created_at ? new Date(s.created_at + "Z").toLocaleString() : "-"}</td>
                    <td className="adm-td text-text-sub text-xs">{s.completed_at ? new Date(s.completed_at + "Z").toLocaleString() : "-"}</td>
                    <td className="adm-td text-text-sub">{toTitleCase(s.branch)}</td>
                    <td className="adm-td text-text-sub">{s.year}</td>
                    <td className="adm-td text-text-sub">{s.semester}</td>
                    <td className="adm-td text-text-sub text-xs">{s.email}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {pagination.total_pages > 0 && !isSearchMode && (
  <div className="flex items-center justify-between px-5 py-3" style={{ borderTop: "1px solid rgba(190, 5, 250, 0.1)" }}>
    <p className="text-sm text-text-main">
      Showing <span className="font-semibold">{(pagination.page - 1) * pagination.page_size + 1}–{Math.min(pagination.page * pagination.page_size, pagination.total_records)}</span> of <span className="font-semibold">{pagination.total_records}</span> students
    </p>
    <div className="flex items-center gap-2">
      <button disabled={pagination.page === 1} onClick={() => fetchResults(pagination.page - 1, filters)}
        className="px-3 py-1.5 rounded-lg text-sm font-medium cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        style={{ background: "var(--buttons-background-color)", color: "white", border: "none" }}>
        Prev
      </button>
      <span className="text-sm text-text-main font-medium px-3">
        Page {pagination.page} of {pagination.total_pages}
      </span>
      <button disabled={pagination.page === pagination.total_pages} onClick={() => fetchResults(pagination.page + 1, filters)}
        className="px-3 py-1.5 rounded-lg text-sm font-medium cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        style={{ background: "var(--buttons-background-color)", color: "white", border: "none" }}>
        Next
      </button>
    </div>
  </div>
)}
      </div>
    </>
  );
}

export default StudentsEvaluationResults;